/**
 * Simple Client-Side Router for Cocktail Application
 * Handles routing to individual cocktail pages with modal display
 */

import { Log } from './logger.js';
import { getCocktailId } from './cocktail-state.js';
import { COCKTAIL_EVENTS } from './constants.js';

const _log = Log('router.js');

class CocktailRouter {
  constructor() {
    this.routes = new Map();
    this.currentRoute = null;
    this.isInitialized = false;
    
    // Available cocktails cache (populated after data loads)
    this.cocktailsMap = new Map();
    
    this.setupRoutes();
  }
  
  /**
   * Initialize the router
   */
  init() {
    if (this.isInitialized) return;
    
    _log.green('Initializing cocktail router');
    
    // Listen for browser navigation (back/forward buttons)
    window.addEventListener('popstate', this.handlePopState.bind(this));
    
    // Listen for cocktail data loading
    document.addEventListener('cocktails-loaded', this.handleCocktailsLoaded.bind(this));
    
    // Listen for modal close events to update URL
    document.addEventListener('modal-closed', this.handleModalClosed.bind(this));
    
    // Listen for close-modal events (dispatched by router itself)
    document.addEventListener('close-modal', this.handleCloseModalEvent.bind(this));
    
    this.isInitialized = true;
    
    // Handle initial route on page load
    this.handleRoute(window.location.hash || window.location.pathname);
  }
  
  /**
   * Set up route patterns
   */
  setupRoutes() {
    // Hash-based routes (easier to implement, works without server config)
    this.routes.set('home', {
      pattern: /^#?\/?$/,
      handler: this.handleHomeRoute.bind(this)
    });
    
    this.routes.set('cocktail', {
      pattern: /^#?\/cocktail\/([^\/]+)$/,
      handler: this.handleCocktailRoute.bind(this)
    });
    
    _log.blue('Routes configured:', Array.from(this.routes.keys()));
  }
  
  /**
   * Handle browser back/forward navigation
   */
  handlePopState(event) {
    _log.blue('Browser navigation detected');
    const path = window.location.hash || window.location.pathname;
    this.handleRoute(path);
  }
  
  /**
   * Handle route changes
   */
  handleRoute(path) {
    _log.green('Handling route:', path);
    
    for (const [routeName, route] of this.routes) {
      const match = path.match(route.pattern);
      if (match) {
        _log.blue('Route matched:', routeName, 'with params:', match.slice(1));
        this.currentRoute = routeName;
        route.handler(...match.slice(1));
        return;
      }
    }
    
    // No route matched, redirect to home
    _log.yellow('No route matched for:', path, 'redirecting to home');
    this.navigateToHome();
  }
  
  /**
   * Handle home route
   */
  handleHomeRoute() {
    _log.green('Showing home page');
    // Close any open modals
    this.closeModal();
  }
  
  /**
   * Handle cocktail detail route
   */
  handleCocktailRoute(cocktailSlug) {
    _log.green('Showing cocktail:', cocktailSlug);
    
    // Check if cocktails are loaded yet
    if (this.cocktailsMap.size === 0) {
      _log.yellow('Cocktails not loaded yet, waiting for data...');
      // Store the pending route to handle after cocktails load
      this.pendingCocktailSlug = cocktailSlug;
      return;
    }
    
    // Find cocktail by slug or ID
    const cocktail = this.findCocktailBySlug(cocktailSlug);
    
    if (cocktail) {
      this.openCocktailModal(cocktail);
    } else {
      _log.yellow('Cocktail not found:', cocktailSlug);
      // Redirect to home if cocktail not found
      this.navigateToHome();
    }
  }
  
  /**
   * Find cocktail by slug (ID or name-based)
   */
  findCocktailBySlug(slug) {
    _log.blue('Looking for cocktail with slug:', slug);
    _log.blue('Available cocktails in map:', this.cocktailsMap.size);
    
    // First try exact ID match
    if (this.cocktailsMap.has(slug)) {
      const found = this.cocktailsMap.get(slug);
      _log.green('Found cocktail by ID:', found.name);
      return found;
    }
    
    // Then try to find by full slug (name-id format)
    for (const [id, cocktail] of this.cocktailsMap) {
      const fullSlug = this.createCocktailSlug(cocktail);
      _log.blue('Comparing slug:', slug, 'with:', fullSlug);
      if (fullSlug === slug) {
        _log.green('Found cocktail by full slug:', cocktail.name);
        return cocktail;
      }
    }
    
    // Finally try by name slug only
    for (const [id, cocktail] of this.cocktailsMap) {
      const nameSlug = this.createSlugFromName(cocktail.name);
      _log.blue('Comparing slug:', slug, 'with name slug:', nameSlug);
      if (nameSlug === slug) {
        _log.green('Found cocktail by name slug:', cocktail.name);
        return cocktail;
      }
    }
    
    _log.yellow('No cocktail found for slug:', slug);
    return null;
  }
  
  /**
   * Create URL-friendly slug from cocktail name
   */
  createSlugFromName(name) {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-')          // Replace spaces with dashes
      .replace(/-+/g, '-')           // Remove multiple consecutive dashes
      .trim('-');                    // Remove leading/trailing dashes
  }
  
  /**
   * Create full slug with ID for uniqueness
   */
  createCocktailSlug(cocktail) {
    const id = getCocktailId(cocktail);
    const nameSlug = this.createSlugFromName(cocktail.name);
    return `${nameSlug}-${id}`;
  }
  
  /**
   * Open cocktail modal
   */
  openCocktailModal(cocktail) {
    _log.green('Opening modal for cocktail:', cocktail.name);
    
    // Dispatch event to open modal
    document.dispatchEvent(new CustomEvent(COCKTAIL_EVENTS.OPEN_MODAL, {
      detail: { cocktail }
    }));
  }
  
  /**
   * Close modal
   */
  closeModal() {
    // Dispatch event to close modal
    document.dispatchEvent(new CustomEvent('close-modal'));
  }
  
  /**
   * Navigate to specific cocktail
   */
  navigateToCocktail(cocktail) {
    _log.green('navigateToCocktail called with:', cocktail?.name);
    
    if (!cocktail) {
      _log.red('No cocktail provided to navigateToCocktail');
      return;
    }
    
    const id = getCocktailId(cocktail);
    _log.blue('Cocktail ID:', id);
    
    const slug = this.createCocktailSlug(cocktail);
    const url = `#/cocktail/${slug}`;
    
    _log.green('Navigating to cocktail URL:', url, 'with slug:', slug);
    
    // Update browser URL without triggering navigation
    window.history.pushState({ cocktail: cocktail.name }, cocktail.name, url);
    this.handleRoute(url);
  }
  
  /**
   * Navigate to home
   */
  navigateToHome() {
    const url = '#/';
    
    _log.green('Navigating to home:', url);
    
    window.history.pushState({ page: 'home' }, 'Cocktails', url);
    this.handleRoute(url);
  }
  
  /**
   * Handle cocktail data loading
   */
  handleCocktailsLoaded(event) {
    const cocktails = event.detail?.cocktails || [];
    _log.green('Updating cocktails map with', cocktails.length, 'cocktails');
    
    // Build cocktails map for quick lookup
    this.cocktailsMap.clear();
    cocktails.forEach(cocktail => {
      const id = getCocktailId(cocktail);
      if (id) {
        this.cocktailsMap.set(id, cocktail);
      }
    });
    
    _log.blue('Cocktails map populated with', this.cocktailsMap.size, 'entries');
    
    // Handle any pending cocktail route that was waiting for data
    if (this.pendingCocktailSlug) {
      _log.green('Handling pending cocktail route:', this.pendingCocktailSlug);
      this.handleCocktailRoute(this.pendingCocktailSlug);
      this.pendingCocktailSlug = null;
    } else if (this.currentRoute === 'cocktail') {
      // Re-handle current route in case we were waiting for data
      this.handleRoute(window.location.hash || window.location.pathname);
    }
  }
  
  /**
   * Handle modal close events
   */
  handleModalClosed(event) {
    _log.green('Modal closed, updating URL to home');
    
    // Update URL to home without triggering route handler
    const url = '#/';
    window.history.pushState({ page: 'home' }, 'Cocktails', url);
    this.currentRoute = 'home';
  }
  
  /**
   * Handle close-modal events (dispatched by router itself)
   */
  handleCloseModalEvent(event) {
    _log.green('Close modal event received');
    // This is handled by the modal component directly
    // No need to do anything here, just acknowledge the event
  }
  
  /**
   * Get shareable URL for cocktail
   */
  getCocktailUrl(cocktail) {
    const slug = this.createCocktailSlug(cocktail);
    const baseUrl = window.location.origin + window.location.pathname;
    return `${baseUrl}#/cocktail/${slug}`;
  }
  
  /**
   * Get current route info
   */
  getCurrentRoute() {
    return {
      route: this.currentRoute,
      path: window.location.hash || window.location.pathname,
      cocktailsLoaded: this.cocktailsMap.size > 0
    };
  }
}

// Create singleton instance
export const cocktailRouter = new CocktailRouter();

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => cocktailRouter.init());
} else {
  cocktailRouter.init();
}

export default cocktailRouter;
